import { useCallback, useState } from 'react'

import { ConfirmModal, useModalContext } from '@fourthwall/components'
import { useDashboardStorage } from '@hooks/useDashboardStorage'

const KEYS = [
  'campaign',
  'addReferralLinkToHomepage',
  'createProduct',
  'creatorApp',
  'creatorChallenge',
  'digitalProduct',
  'domain',
  'freeShipping',
  'googleAnalytics',
  'helpCenter',
  'integrations',
  'invite',
  'joinDiscord',
  'memberships',
  'orderSamples',
  'ordersToSend',
  'payouts',
  'promotions',
  'referral',
  'social',
  'thankYou',
  'twitchGifting',
  'twitchPromotion',
  'youTubeProductShelf',
] as const

type Key = (typeof KEYS)[number]

type Visibility = Record<Key, boolean>

type RecommendationsDismiss = {
  [K in Key]: {
    visible: boolean
    dismiss: () => void
  }
}

export const useRecommendationsDismiss = () => {
  const { dashboardStorage } = useDashboardStorage()
  const { open } = useModalContext()
  const initialVisibility = KEYS.reduce<Visibility>(
    (acc, key) => ({
      ...acc,
      [key]: !dashboardStorage.get(`recommendationsDismiss.${key}`),
    }),
    {} as Visibility,
  )
  const [visibility, setVisibility] = useState<Visibility>(initialVisibility)

  const dismiss = (key: Key) => {
    setVisibility((prev) => ({ ...prev, [key]: false }))
    dashboardStorage.set(`recommendationsDismiss.${key}`, true)
  }

  const recommendationsDismiss = KEYS.reduce<RecommendationsDismiss>(
    (acc, key) => ({
      ...acc,
      [key]: { visible: visibility[key], dismiss: () => dismiss(key) },
    }),
    {} as RecommendationsDismiss,
  )

  const createOnDismiss = (onConfirm: () => void) => () => {
    open(ConfirmModal, {
      title: 'Are you sure you want to hide this recommendation?',
      text: 'This action cannot be reversed.',
      confirmLabel: 'Hide recommendation',
      onConfirm,
    })
  }

  const createOnDismissCallback = useCallback(
    (onConfirm: () => void) => {
      return () => {
        open(ConfirmModal, {
          title: 'Are you sure you want to hide this recommendation?',
          text: 'This action cannot be reversed.',
          confirmLabel: 'Hide recommendation',
          onConfirm,
        })
      }
    },
    [open],
  )

  return { recommendationsDismiss, createOnDismiss, createOnDismissCallback }
}
